<template>
  <section class="admin-content">
    <Navbar />
    <main class="admin-main">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center justify-content-between text-white" style="padding: 32px 0;">
            <div class="d-flex align-items-center" style="gap: 16px;">
              <div class="header-icon-wrap">
                <i class="fe fe-user" style="font-size: 22px;"></i>
              </div>
              <div>
                <h4 class="mb-0 font-weight-bold">{{ auth_user.full_name || 'Enrollee Profile' }}</h4>
                <div class="d-flex align-items-center mt-1" style="gap: 10px;">
                  <small style="opacity: 0.8;">{{ auth_user.id_card_number }}</small>
                  <span v-if="auth_user.status == 'active'" class="header-status--active">
                    <i class="fe fe-check-circle mr-1"></i> Active
                  </span>
                  <span v-else-if="auth_user.status" class="header-status--inactive">
                    <i class="fe fe-alert-circle mr-1"></i> Inactive
                  </span>
                </div>
              </div>
            </div>
            <button class="btn btn-outline-light btn-sm" onclick="history.back()">
              <i class="fe fe-arrow-left mr-1"></i> Back
            </button>
          </div>
        </div>
      </div>

      <section style="margin-top: -32px;">
        <div class="container pb-5">
          <div class="row">

            <!-- ── Photo / Identity Card ── -->
            <div class="col-lg-4 mb-4">
              <div class="card shadow h-100" style="border-radius: 14px; overflow: hidden; border: none;">
                <!-- Gradient top strip -->
                <div style="height: 80px; background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);"></div>

                <div class="card-body text-center pt-0">
                  <!-- Avatar -->
                  <div style="margin-top: -48px;" class="mb-3">
                    <img
                      :src="auth_user.image_url"
                      class="rounded-circle"
                      style="width: 96px; height: 96px; object-fit: cover; border: 4px solid #fff; box-shadow: 0 4px 16px rgba(0,0,0,0.12);"
                      onerror="this.onerror=null; this.src='/assets/img/zamfara.jpg'"
                    />
                  </div>

                  <h5 class="font-weight-bold mb-1">{{ auth_user.firstname }} {{ auth_user.lastname }}</h5>
                  <p class="text-muted mb-2" style="font-size: 0.82rem;">Enrollee</p>

                  <div class="mb-3">
                    <span class="code-chip">{{ auth_user.id_card_number }}</span>
                  </div>

                  <span v-if="auth_user.status == 'active'"
                    class="badge px-3 py-2" style="background: #d1fae5; color: #065f46; border-radius: 20px; font-size: 0.78rem;">
                    <i class="fe fe-check-circle mr-1"></i> Active
                  </span>
                  <span v-else-if="auth_user.status"
                    class="badge px-3 py-2" style="background: #fef3c7; color: #92400e; border-radius: 20px; font-size: 0.78rem;">
                    <i class="fe fe-alert-circle mr-1"></i> Inactive
                  </span>

                  <!-- Gender pill -->
                  <div v-if="auth_user.gender" class="mt-3">
                    <span class="badge px-3 py-1" style="background: #eef2ff; color: #1e40af; border-radius: 20px; font-size: 0.78rem;">
                      <i class="fe fe-user mr-1"></i> {{ auth_user.gender }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── Profile Details ── -->
            <div class="col-lg-8 mb-4">
              <div class="card shadow table-card">
                <div class="form-card-header">
                  <div class="section-number"><i class="fe fe-info" style="font-size: 12px;"></i></div>
                  <h6 class="mb-0 font-weight-bold text-white">Enrollee Details</h6>
                </div>

                <div class="card-body px-4 py-3">
                  <div class="info-row">
                    <span class="info-label"><i class="fe fe-user info-icon"></i> Full Name</span>
                    <span class="info-value">{{ auth_user.firstname }} {{ auth_user.lastname }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label"><i class="fe fe-credit-card info-icon"></i> Enrollee ID</span>
                    <span class="info-value">
                      <span class="code-chip">{{ auth_user.id_card_number }}</span>
                    </span>
                  </div>
                  <div class="info-row">
                    <span class="info-label"><i class="fe fe-phone info-icon"></i> Phone Number</span>
                    <span class="info-value">{{ auth_user.phone_number || '—' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label"><i class="fe fe-gift info-icon"></i> Date of Birth</span>
                    <span class="info-value">{{ auth_user.dob | moment("dddd, MMMM Do YYYY") }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label"><i class="fe fe-user info-icon"></i> Gender</span>
                    <span class="info-value">{{ auth_user.gender || '—' }}</span>
                  </div>
                  <div class="info-row" v-if="auth_user.userprovider">
                    <span class="info-label"><i class="fe fe-home info-icon"></i> Provider</span>
                    <span class="info-value">{{ auth_user.userprovider.agency_name }}</span>
                  </div>
                  <div class="info-row" v-if="auth_user.usertpa">
                    <span class="info-label"><i class="fe fe-shield info-icon"></i> HMO</span>
                    <span class="info-value">{{ auth_user.usertpa.organization_name }}</span>
                  </div>
                  <div class="info-row" v-if="auth_user.expiry_date">
                    <span class="info-label"><i class="fe fe-calendar info-icon"></i> Expiry Date</span>
                    <span class="info-value">{{ auth_user.expiry_date | moment("MMM Do YYYY") }}</span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <div class="vld-parent">
          <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
        </div>
      </section>

      <!-- ── Webcam Photo Modal (legacy) ── -->
      <div class="modal fade" id="example_01" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg" role="document">
          <div class="modal-content" style="border-radius: 12px; overflow: hidden; border: none;">
            <div class="modal-header" style="background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); border-bottom: none;">
              <h5 class="modal-title text-white font-weight-bold">
                <i class="fe fe-camera mr-2"></i> Capture Photo
              </h5>
              <button type="button" class="close text-white" data-dismiss="modal" aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div class="modal-body p-4">
              <video id="video" width="100%" height="auto" autoplay class="rounded mb-3"></video>
              <canvas id="canvas" width="720px" height="550px" class="d-none"></canvas>
            </div>
            <div class="modal-footer" style="border-top: 1px solid #e9ecef;">
              <button type="button" class="btn btn-outline-secondary btn-sm" data-dismiss="modal">Close</button>
              <button @click="takePic" class="btn btn-outline-primary btn-sm">
                <i class="fe fe-camera mr-1"></i> Snap Photo
              </button>
              <button type="button" class="btn btn-submit btn-sm" @click="savePic">
                <i class="fe fe-save mr-1"></i> Save Photo
              </button>
            </div>
          </div>
        </div>
      </div>

    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";

export default {
  components: { Navbar, Loading },

  data() {
    return {
      user: null,
      auth_user: "",
      notes: "",
      medications: "",
      image: "",
      edit: false,
      isLoading: false,
      fullPage: true,
      agency_id: "",
      imagefile: "",
      pictureShower: true,
    };
  },

  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.axios
      .get(`/api/v1/auth/getSpeicifDependant/${this.$route.params.id}`)
      .then((response) => { this.auth_user = response.data.data; })
      .catch((error) => { console.error(error); });
  },

  methods: {
    attachPic(event) {
      this.image = event.target.files[0];
      this.uploadPic();
    },

    uploadPic() {
      this.isLoading = true;
      var formData = new FormData();
      formData.append("user_image", this.image);
      formData.append("dependant_id", this.$route.params.id);
      this.axios
        .post("/api/v1/auth/uploadDependantImage", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          console.log(response);
          this.isLoading = false;
          this.$toasted.success("Profile image updated!", { position: "top-center", duration: 3000 });
          this.fetchUser();
        });
    },

    takePic() {
      var video = document.getElementById("video");
      var canvas = document.getElementById("canvas");
      var context = canvas.getContext("2d");
      context.drawImage(video, 0, 0, 640, 480);
      var image = new Image();
      image.src = canvas.toDataURL("image/png");
      this.imagefile = image.src;
    },

    savePic() {
      this.axios
        .post(`/api/v1/auth/uploadcustomerpicImage`, {
          user_image: this.imagefile,
          user_id: this.$route.params.id,
        })
        .then((response) => {
          console.log(response);
          this.$toasted.success("Profile image saved!", { position: "top-center", duration: 3000 });
          this.fetchUser();
        })
        .catch((error) => { console.error(error); });
    },

    fetchUser() {
      this.axios
        .get(`/api/v1/user-no-auth/${this.$route.params.id}`)
        .then((response) => { this.auth_user = response.data.user; })
        .catch((error) => { console.error(error); });
    },
  },

  created() {
    this.fetchUser();
  },
};
</script>
